import React, { useState } from "react";
import { ChevronDown, ChevronRight, Plus, Trash2, Pencil, Check, X, Loader, User } from "lucide-react";

// Die Kontrollen der Schlusspruefung, aenderbar. Jede laeuft als eigener
// Aufruf mit eigenem Auftrag - deshalb ist hier auch der Auftrag sichtbar und
// nicht nur ein Name.

const LEERES_BEISPIEL = { schlecht: "", gut: "", warum: "" };

function BeispielFelder({ beispiele, onChange }) {
  const setze = (i, feld, wert) =>
    onChange(beispiele.map((b, j) => (i === j ? { ...b, [feld]: wert } : b)));
  return (
    <div className="gate-beispiele-edit">
      {beispiele.map((b, i) => (
        <div className="gate-beispiel-edit" key={i}>
          <div className="gate-beispiel-kopf">
            <span>Beispiel {i + 1}</span>
            <button className="gate-mini-btn" onClick={() => onChange(beispiele.filter((_, j) => j !== i))}
              aria-label="Beispiel entfernen"><X size={12} /></button>
          </div>
          <input className="ai-prompt-input" placeholder="So nicht …" value={b.schlecht}
            onChange={(e) => setze(i, "schlecht", e.target.value)} />
          <input className="ai-prompt-input" placeholder="Sondern so …" value={b.gut}
            onChange={(e) => setze(i, "gut", e.target.value)} />
          <input className="ai-prompt-input" placeholder="Warum das besser ist" value={b.warum}
            onChange={(e) => setze(i, "warum", e.target.value)} />
        </div>
      ))}
      <button className="btn-secondary-sm" onClick={() => onChange([...beispiele, { ...LEERES_BEISPIEL }])}>
        <Plus size={13} /> Beispiel
      </button>
    </div>
  );
}

function GateKarte({ gate, onGeaendert, onGeloescht }) {
  const [offen, setOffen] = useState(false);
  const [bearbeiten, setBearbeiten] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState("");
  const [entwurf, setEntwurf] = useState(null);

  const starteBearbeiten = () => {
    setEntwurf({
      label: gate.label || "",
      beschreibung: gate.beschreibung || "",
      pruefauftrag: gate.pruefauftrag || "",
      beispiele: Array.isArray(gate.beispiele) ? gate.beispiele : [],
    });
    setBearbeiten(true);
    setOffen(true);
    setFehler("");
  };

  const senden = async (koerper) => {
    setBusy(true);
    setFehler("");
    try {
      const res = await fetch(`/api/pruef-gates/${gate.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(koerper),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Speichern fehlgeschlagen");
      onGeaendert(d);
      return true;
    } catch (e) {
      setFehler(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const speichern = async () => {
    if (await senden(entwurf)) setBearbeiten(false);
  };

  const loeschen = async () => {
    if (!window.confirm(
      `Kontrolle „${gate.label}" in den Papierkorb verschieben? Sie läuft dann nicht mehr mit. `
      + "Du kannst sie dort 30 Tage lang wiederherstellen."
    )) return;
    setBusy(true);
    try {
      const res = await fetch(`/api/pruef-gates/${gate.id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("Löschen fehlgeschlagen");
      onGeloescht(gate.id);
    } catch (e) {
      setFehler(e.message);
      setBusy(false);
    }
  };

  const beispiele = Array.isArray(gate.beispiele) ? gate.beispiele : [];

  return (
    <div className={`gate-karte ${gate.aktiv ? "" : "gate-karte--aus"}`}>
      <div className="gate-kopf" role="button" tabIndex={0}
        onClick={() => !bearbeiten && setOffen(!offen)}
        onKeyDown={(e) => { if (e.key === "Enter" && !bearbeiten) setOffen(!offen); }}>
        {offen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="gate-name">{gate.label}</span>
        {gate.zuletzt && (
          <span className="gate-tag gate-tag--zuletzt"><User size={11} /> läuft zuletzt</span>
        )}
        {!gate.aktiv && <span className="gate-tag">abgeschaltet</span>}
        <span className="gate-kopf-spacer" />
        <button className="gate-mini-btn" title={gate.aktiv ? "Abschalten" : "Einschalten"}
          onClick={(e) => { e.stopPropagation(); senden({ aktiv: !gate.aktiv }); }} disabled={busy}>
          {busy ? <Loader size={13} className="icon-spin" /> : gate.aktiv ? <Check size={13} /> : <X size={13} />}
        </button>
        <button className="gate-mini-btn" title="Bearbeiten"
          onClick={(e) => { e.stopPropagation(); starteBearbeiten(); }}><Pencil size={13} /></button>
        <button className="gate-mini-btn" title="Löschen"
          onClick={(e) => { e.stopPropagation(); loeschen(); }} disabled={busy}><Trash2 size={13} /></button>
      </div>

      {offen && (
        <div className="gate-inhalt">
          {fehler && <div className="form-error">{fehler}</div>}

          {bearbeiten ? (
            <>
              <label className="gate-feld-label">Name</label>
              <input className="ai-prompt-input" value={entwurf.label}
                onChange={(e) => setEntwurf({ ...entwurf, label: e.target.value })} />

              <label className="gate-feld-label">Wofür diese Kontrolle da ist</label>
              <textarea className="ai-prompt-input email-textarea" rows={3} value={entwurf.beschreibung}
                placeholder="In einem Satz: worauf achtet diese Kontrolle?"
                onChange={(e) => setEntwurf({ ...entwurf, beschreibung: e.target.value })} />

              <label className="gate-feld-label">
                Prüfauftrag – das bekommt der Agent wörtlich zu lesen
              </label>
              <textarea className="ai-prompt-input email-textarea" rows={8} value={entwurf.pruefauftrag}
                placeholder="Sag dem Agenten, was er markieren soll und was nicht."
                onChange={(e) => setEntwurf({ ...entwurf, pruefauftrag: e.target.value })} />

              <label className="gate-feld-label">Beispiele</label>
              <BeispielFelder beispiele={entwurf.beispiele}
                onChange={(b) => setEntwurf({ ...entwurf, beispiele: b })} />

              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => { setBearbeiten(false); setFehler(""); }}>
                  Abbrechen
                </button>
                <button className="btn-primary" onClick={speichern} disabled={busy}>
                  {busy ? <><Loader size={14} className="icon-spin" /> Speichere …</> : "Speichern"}
                </button>
              </div>
            </>
          ) : (
            <>
              {gate.beschreibung && <p className="gate-beschreibung">{gate.beschreibung}</p>}

              <div className="gate-abschnitt">
                <div className="gate-abschnitt-titel">Prüfauftrag</div>
                <pre className="gate-auftrag">{gate.pruefauftrag}</pre>
              </div>

              {beispiele.length > 0 && (
                <div className="gate-abschnitt">
                  <div className="gate-abschnitt-titel">Beispiele</div>
                  {beispiele.map((b, i) => (
                    <div className="gate-beispiel" key={i}>
                      {b.schlecht && <div className="gate-bsp-schlecht"><X size={12} /> {b.schlecht}</div>}
                      {b.gut && <div className="gate-bsp-gut"><Check size={12} /> {b.gut}</div>}
                      {b.warum && <div className="gate-bsp-warum">{b.warum}</div>}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function NeueGateKarte({ phase, onAngelegt }) {
  const [offen, setOffen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState("");
  const [entwurf, setEntwurf] = useState({ label: "", beschreibung: "", pruefauftrag: "", beispiele: [] });

  const anlegen = async () => {
    setBusy(true);
    setFehler("");
    try {
      const res = await fetch("/api/pruef-gates", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...entwurf, phase }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Anlegen fehlgeschlagen");
      onAngelegt(d);
      setEntwurf({ label: "", beschreibung: "", pruefauftrag: "", beispiele: [] });
      setOffen(false);
    } catch (e) {
      setFehler(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!offen) {
    return (
      <button className="btn-secondary gate-neu-btn" onClick={() => setOffen(true)}>
        <Plus size={14} /> Kontrolle hinzufügen
      </button>
    );
  }

  return (
    <div className="gate-karte gate-karte--neu">
      <div className="gate-inhalt">
        {fehler && <div className="form-error">{fehler}</div>}

        <label className="gate-feld-label">Name</label>
        <input className="ai-prompt-input" placeholder="z. B. Aktiv statt passiv" value={entwurf.label}
          onChange={(e) => setEntwurf({ ...entwurf, label: e.target.value })} />

        <label className="gate-feld-label">Wofür diese Kontrolle da ist</label>
        <textarea className="ai-prompt-input email-textarea" rows={2} value={entwurf.beschreibung}
          placeholder="In einem Satz: worauf achtet sie?"
          onChange={(e) => setEntwurf({ ...entwurf, beschreibung: e.target.value })} />

        <label className="gate-feld-label">
          Prüfauftrag – das bekommt der Agent wörtlich zu lesen
        </label>
        <textarea className="ai-prompt-input email-textarea" rows={7} value={entwurf.pruefauftrag}
          placeholder={"Sag dem Agenten, was er markieren soll.\n\nBeispiel:\nMarkiere jeden Satz im Passiv und schlage eine aktive Fassung vor. Nenne die Stelle im Wortlaut."}
          onChange={(e) => setEntwurf({ ...entwurf, pruefauftrag: e.target.value })} />

        <label className="gate-feld-label">Beispiele (optional)</label>
        <BeispielFelder beispiele={entwurf.beispiele}
          onChange={(b) => setEntwurf({ ...entwurf, beispiele: b })} />

        <div className="modal-actions">
          <button className="btn-secondary" onClick={() => { setOffen(false); setFehler(""); }}>Abbrechen</button>
          <button className="btn-primary" onClick={anlegen} disabled={busy}>
            {busy ? <><Loader size={14} className="icon-spin" /> Lege an …</> : "Kontrolle anlegen"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PruefGates({ gates, phase, titel, onChange }) {
  const fach = gates.filter((g) => !g.zuletzt);
  const zuletzt = gates.filter((g) => g.zuletzt);
  const aktiveFach = fach.filter((g) => g.aktiv).length;

  const ersetze = (g) => onChange(gates.map((x) => (x.id === g.id ? g : x)));
  const entferne = (id) => onChange(gates.filter((x) => x.id !== id));

  return (
    <div className="gates-block">
      <div className="gates-kopf">
        <strong>{titel || "Diese Kontrollen laufen"}</strong>
        <span className="gates-zahl">
          {aktiveFach} gleichzeitig{zuletzt.some((g) => g.aktiv) ? ", dann der Zielgruppen-Check" : ""}
        </span>
      </div>
      <p className="gates-hinweis">
        Jede Kontrolle ist ein eigener Agent mit eigenem Auftrag. Klick eine an, um zu sehen,
        worauf sie achtet – und ändere den Auftrag, wenn sie etwas übersieht.
      </p>

      {fach.map((g) => (
        <GateKarte key={g.id} gate={g} onGeaendert={ersetze} onGeloescht={entferne} />
      ))}

      <NeueGateKarte phase={phase} onAngelegt={(g) => onChange([...gates, g])} />

      {zuletzt.length > 0 && (
        <>
          <div className="gates-trenner">
            <span>und ganz zum Schluss, wenn alles andere sauber ist</span>
          </div>
          {zuletzt.map((g) => (
            <GateKarte key={g.id} gate={g} onGeaendert={ersetze} onGeloescht={entferne} />
          ))}
        </>
      )}
    </div>
  );
}
